
import React, { Component } from 'react';
import type { Dispatch } from 'redux';

import { isMobileBrowser } from '../../../base/environment/utils';
import { translate } from '../../../base/i18n';
import { Icon, IconArrowDown, IconArrowUp, IconWarning } from '../../../base/icons';
import { getLocalParticipant } from '../../../base/participants';
import { connect } from '../../../base/redux';
import { ToolboxButtonWithIcon } from '../../../base/toolbox/components';
import Tooltip from '../../../base/tooltip/components/Tooltip';
import { getLocalMHVideoTrack } from '../../../base/tracks';
import { VideoSettingsPopup, toggleVideoSettings } from '../../../settings';
import { isVideoSettingsButtonDisabled } from '../../functions';
import { requestMediaPermissions, showPermissionGuidance } from '../../utils/permissionUtils';
import VideoMuteButton from '../VideoMuteButton';

type Props = {

  /**
   * Click handler for the small icon. Opens video options.
   */
  onVideoOptionsClick: Function,

  /**
   * Indicates whether video permissions have been granted or denied.
   */
  hasPermissions: boolean,

  /**
   * Whether there is a video track or not.
   */
  hasVideoTrack: boolean,

  /**
   * If the button should be disabled.
   */
  isDisabled: boolean,

  /**
   * Flag controlling the visibility of the button.
   * VideoSettings popup is currently disabled on mobile browsers
   * as mobile devices do not support capture of more than one
   * camera at a time.
   */
  visible: boolean,

  /**
   * Used for translation.
   */
  t: Function,

  /**
   * State of the confernece.
   */
  _state: any,

  /**
   * The redux {@code dispatch} function.
   */
  dispatch: Dispatch<any>,
};

/**
 * Button used for video & video settings.
 *
 * @returns {ReactElement}
 */
class VideoSettingsButton extends Component<Props> {
    state = {
        permissionRequestInProgress: false
    };

    /**
   * Returns true if the settings icon is disabled.
   *
   * @returns {boolean}
   */
    _isIconDisabled() {
        const { hasPermissions, hasVideoTrack, isDisabled } = this.props;

        return (!hasPermissions && !hasVideoTrack) || Boolean(isDisabled);
    }

    /**
   * Handles clicking of the disabled button.
   *
   * @returns {void}
   */
    _onDisabledClick = () => {
        const { dispatch, _state } = this.props;

        dispatch(toggleVideoSettings(_state));
    };

    /**
       * Calls into legacy UI to update the application layout, if necessary.
       *
       * @inheritdoc
       * returns {void}
       */
    componentDidUpdate() {
        // No auto-muting side effects.
    }

    /**
   * Implements React's {@link Component#render}.
   *
   * @inheritdoc
   */
    render() {
        const { hasVideoTrack, onVideoOptionsClick, t, _videoSettingsVisible, _isVideoLocked } = this.props;

        const { isDisabled } = this.props;
        const isIconDisabled = this._isIconDisabled();
        const showWarning = isIconDisabled && !hasVideoTrack;

        const tooltip = _isVideoLocked
            ? t('toolbar.videoLocked')
            : isDisabled
                ? t('toolbar.cameraNotPluggedIn')
                : isIconDisabled
                    ? t('toolbar.videoUnavailable')
                    : t('toolbar.videoSettings');

        return showWarning ? (
            <Tooltip
                content = { tooltip }
                position = { 'top' }>
                <div
                    className = 'IconWarningButtonParent'
                    onClick = { this.state.permissionRequestInProgress ? undefined : this._onPermissionRequest }>
                    <div className = 'IconWarningButton'>
                        <Icon src = { IconWarning } />
                    </div>
                    <VideoMuteButton />
                </div>
            </Tooltip>
        ) : isMobileBrowser() ? (
            <Tooltip
                content = { tooltip }
                position = { 'top' }>
                <div className = 'settings-button-child'>
                    <VideoMuteButton />
                </div>
            </Tooltip>
        ) : (
            <VideoSettingsPopup>
                <Tooltip
                    content = { tooltip }
                    position = { 'top' }>
                    <ToolboxButtonWithIcon
                        icon = { _videoSettingsVisible ? IconArrowDown : IconArrowUp }
                        iconDisabled = { _isVideoLocked }
                        onIconClick = { onVideoOptionsClick }>
                        <VideoMuteButton />
                    </ToolboxButtonWithIcon>
                </Tooltip>
            </VideoSettingsPopup>
        );
    }

    /**
     * Handles permission request click.
     *
     * @private
     * @returns {void}
     */
    _onPermissionRequest = async () => {
        this.setState({ permissionRequestInProgress: true });

        try {
            // audio must be requested explicitly: it defaults to true, so asking for the camera also asked for the
            // microphone and a missing/denied microphone failed the whole request - the camera permission this
            // button exists for was never granted.
            const result = await requestMediaPermissions({ audio: false,
                video: true });

            if (result.showGuidance) {
                // Show the permission guidance dialog with browser-specific instructions
                showPermissionGuidance([ 'video' ]);
            }
        } finally {
            this.setState({ permissionRequestInProgress: false });
        }
    };
}

/**
 * Function that maps parts of Redux state tree into component props.
 *
 * @param {Object} state - Redux state.
 * @returns {Object}
 */
function mapStateToProps(state) {
    const { permissions = {} } = state['features/base/devices'];
    const { videoSettingsVisible } = state['features/settings'];
    const localParticipantID = getLocalParticipant(state)?.id;
    const { lockVideo } = state['features/base/conference'];
    const isMuted = state['features/base/media']?.video?.muted > 0;

    return {
        hasPermissions: permissions.video,
        hasVideoTrack: Boolean(getLocalMHVideoTrack(state)),
        isDisabled: isVideoSettingsButtonDisabled(state),
        visible: !isMobileBrowser(),
        _isVideoLocked: lockVideo[`${localParticipantID}`],
        _state: state,
        isMuted,
        _videoSettingsVisible: videoSettingsVisible
    };
}

const mapDispatchToProps = {
    onVideoOptionsClick: toggleVideoSettings
};

export default translate(
  connect(mapStateToProps, mapDispatchToProps)(VideoSettingsButton)
);
